import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import HeroCarousel from '../Common/HeroCarousel';
import { FaUser, FaEnvelope, FaPhone, FaHome, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import { submitMinistryRequest } from '../../Redux/slices/ministrySlice';

const JoinUs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, submitSuccess } = useSelector(state => state.ministry);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    ministry: '',
    experience: '',
    availability: [],
    whyJoin: '',
    previousMinistry: '',
    skills: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const ministries = [
    { value: 'worship-team', label: 'Worship Team' },
    { value: 'media-ministry', label: 'Media Ministry' },
    { value: 'creative-arts', label: 'Creative Arts' },
    { value: 'children-ministry', label: 'Children\'s Ministry (Ages 0-12)' },
    { value: 'youth-ministry', label: 'Youth Ministry (Ages 13-18)' },
    { value: 'adult-ministry', label: 'Adult Ministry (Ages 18+)' },
    { value: 'community-outreach', label: 'Community Outreach' },
    { value: 'missions', label: 'Missions Ministry' },
    { value: 'pastoral-care', label: 'Pastoral Care' },
    { value: 'discipleship', label: 'Discipleship Training' },
    { value: 'small-groups', label: 'Small Groups' },
    { value: 'ushering', label: 'Ushering & Hospitality' },
    { value: 'prayer-team', label: 'Prayer Team' }
  ];

  const availabilityOptions = [
    'Sunday Morning Service',
    'Wednesday Evening Service',
    'Friday Night Vigil',
    'Weekday Events',
    'Special Programs'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name === 'availability') {
      const updatedAvailability = checked
        ? [...formData.availability, value]
        : formData.availability.filter(item => item !== value);
      
      setFormData({
        ...formData,
        availability: updatedAvailability
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.ministry) newErrors.ministry = 'Please select a ministry';
    if (formData.availability.length === 0) newErrors.availability = 'Please select at least one availability';
    if (!formData.whyJoin.trim()) newErrors.whyJoin = 'Please tell us why you want to join';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Prepare request data with both ministry value and label
      const selectedMinistry = ministries.find(m => m.value === formData.ministry);
      const requestData = {
        ...formData,
        ministry: formData.ministry, // Keep the value (e.g., "worship-team")
        ministryLabel: selectedMinistry?.label || formData.ministry // Add the label
      };
      
      // Submit to backend via Redux
      const result = await dispatch(submitMinistryRequest(requestData));
      
      if (result.type === 'ministry/submitRequest/fulfilled') {
        setSubmitted(true);
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Scroll to first error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (submitted || submitSuccess) {
    return (
      <>
        <Header />
        <div className="min-h-screen py-20 bg-gray-50">
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto">
              {/* Success Card */}
              <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-10 md:p-14 text-center border border-gray-200">
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg animate-bounce">
                  <FaCheckCircle className="text-white text-5xl" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Application Submitted!</h2>
                <p className="text-gray-600 mb-8 font-sf-pro text-lg leading-relaxed">
                  Thank you for your interest in serving with us! We're excited to have you join our ministry family. 
                  Your application has been received and is pending admin approval.
                </p>
                
                {/* Info Box */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-6 mb-8 text-left">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-lg">✓</span>
                    </div>
                    What Happens Next?
                  </h3>
                  <ul className="space-y-3 text-blue-800 font-sf-pro">
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold mt-1">1.</span>
                      <span>Our admin team will review and approve your application</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold mt-1">2.</span>
                      <span>You'll receive a notification email at <strong>{formData.email}</strong> once approved</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold mt-1">3.</span>
                      <span>Our ministry coordinator will contact you within 3-5 business days to schedule an orientation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold mt-1">4.</span>
                      <span>You'll receive ministry-specific training and begin serving</span>
                    </li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-10 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Back to Home
                  </button>
                  <button
                    onClick={() => navigate('/ministries')}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-10 rounded-xl transition border-2 border-gray-300"
                  >
                    View Ministries
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <HeroCarousel 
        title="Join a Ministry"
        subtitle="Use your gifts and talents to serve God and make a difference in our community"
        height="h-[500px]"
        contentPosition="bottom"
      />

      {/* Form Section */}
      <div className="relative py-16 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/ministries')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 font-sf-pro font-medium transition"
          >
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition shadow-md">
              <FaArrowLeft className="text-white text-sm" />
            </div>
            <span className="text-lg">Back to Ministries</span>
          </button>

          <div className="max-w-5xl mx-auto">
            {/* Introduction Card */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl shadow-2xl p-8 md:p-12 mb-10 text-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <FaCheckCircle className="text-white text-3xl" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Ministry Application</h2>
              </div>
              <p className="text-blue-50 font-sf-pro text-lg mb-6 leading-relaxed">
                We're thrilled that you want to serve! Your gifts and talents are valuable to God's kingdom. 
                Complete this application to join one of our ministries and make an eternal impact.
              </p>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-5">
                <p className="text-sm text-blue-50 font-sf-pro leading-relaxed">
                  <strong className="text-white">What happens next?</strong><br />
                  After submission, our admin team will review and approve your application. Once approved, our ministry coordinator 
                  will contact you within 3-5 business days to schedule an orientation meeting and discuss your role in the ministry.
                </p>
              </div>
            </div>

            {/* Application Form */}
            <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-200">
              {/* Personal Information Section */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FaUser className="text-blue-600 text-lg" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Personal Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.firstName ? 'border-red-500' : 'border-gray-300'
                        } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                        placeholder="John"
                      />
                    </div>
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.lastName ? 'border-red-500' : 'border-gray-300'
                        } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                        placeholder="Doe"
                      />
                    </div>
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaEnvelope className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                        placeholder="john.doe@example.com"
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaPhone className="text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                        placeholder="+234 8125567466"
                      />
                    </div>
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                {/* Address */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaHome className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`block w-full pl-10 pr-3 py-3 border ${
                        errors.address ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                      placeholder="123 Main Street"
                    />
                  </div>
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* City */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 border ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                      placeholder="Lagos"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 border ${
                        errors.state ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                      placeholder="Ogun State"
                    />
                    {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                  </div>
                </div>
              </div>

              {/* Ministry Information Section */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FaCheckCircle className="text-purple-600 text-lg" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Ministry Information</h3>
                </div>

                {/* Ministry Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Which Ministry Would You Like to Join? <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="ministry"
                    value={formData.ministry}
                    onChange={handleChange}
                    className={`block w-full px-4 py-3 border ${
                      errors.ministry ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                  >
                    <option value="">Select a ministry...</option>
                    {ministries.map((ministry) => (
                      <option key={ministry.value} value={ministry.value}>
                        {ministry.label}
                      </option>
                    ))}
                  </select>
                  {errors.ministry && <p className="text-red-500 text-sm mt-1">{errors.ministry}</p>}
                </div>

                {/* Availability */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    When Are You Available to Serve? <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    {availabilityOptions.map((option) => (
                      <label key={option} className="flex items-center">
                        <input
                          type="checkbox"
                          name="availability"
                          value={option}
                          checked={formData.availability.includes(option)}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-gray-700 font-sf-pro">{option}</span>
                      </label>
                    ))}
                  </div>
                  {errors.availability && <p className="text-red-500 text-sm mt-1">{errors.availability}</p>}
                </div>

                {/* Previous Ministry Experience */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Previous Ministry Experience (Optional)
                  </label>
                  <textarea
                    name="previousMinistry"
                    value={formData.previousMinistry}
                    onChange={handleChange}
                    rows="3"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                    placeholder="Tell us about any previous ministry experience you have..."
                  />
                </div>

                {/* Skills and Talents */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Skills and Talents (Optional)
                  </label>
                  <textarea
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    rows="3"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                    placeholder="List any relevant skills, talents, or qualifications (e.g., musical instruments, technical skills, teaching experience, etc.)"
                  />
                </div>

                {/* Why Join */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Why Do You Want to Join This Ministry? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="whyJoin"
                    value={formData.whyJoin}
                    onChange={handleChange}
                    rows="4"
                    className={`block w-full px-4 py-3 border ${
                      errors.whyJoin ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm`}
                    placeholder="Share your motivation and how you hope to contribute to this ministry..."
                  />
                  {errors.whyJoin && <p className="text-red-500 text-sm mt-1">{errors.whyJoin}</p>}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mb-6">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1 ${
                      errors.agreeToTerms ? 'border-red-500' : ''
                    }`}
                  />
                  <span className="ml-2 text-sm text-gray-700 font-sf-pro">
                    I agree to commit to regular attendance, participate in ministry training, 
                    and serve with excellence and integrity. I understand that my application will be 
                    reviewed and I may be contacted for an interview. <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.agreeToTerms && <p className="text-red-500 text-sm mt-1">{errors.agreeToTerms}</p>}
              </div>

              {/* Submit Button */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/ministries')}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-8 rounded-xl transition border border-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default JoinUs;
